import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useAuth } from "../../auth/AuthProvider.js";
import { ApiFetchError, type ApiClient } from "../../lib/api.js";
import { Badge, Button, Card } from "../../components/index.js";
import type { Shipment, WorkflowStatusInfo } from "../../types.js";
import {
  buildBoxLabelCards,
  classifyBox,
  PLAN_CARD_KEY,
  TERMINAL_WORKFLOW_STATUSES,
  type BoxState
} from "../../lib/step4-box-labels.js";
import type { StepProps } from "./step-props.js";

/**
 * Etapa 4 — Etiquetas (Design.md §6.4). Duas famílias de etiquetas:
 *  - FNSKU (item): coladas em CADA unidade. Geradas via createMarketplaceItemLabels
 *    (gate labelOwner = SELLER) SOB DEMANDA ao clicar (a rota lazy gera o PDF e
 *    re-baixa em 404) — disponíveis assim que o Passo 1 fecha. Por isso o botão
 *    FNSKU NÃO é gateado por prontidão.
 *  - Caixa (shipment): coladas em cada caixa. Geradas no FINALIZE (getLabels v0),
 *    que roda ASSÍNCRONO logo após a confirmação do transporte (Passo 3).
 *
 * UMA ETIQUETA POR ENVIO. A Amazon divide o plano em N remessas (placement) e
 * `getLabels` v0 é por `shipmentConfirmationId` — ou seja, N PDFs DIFERENTES, um
 * por remessa, exatamente como o Seller Central, que mostra um botão "Print box
 * labels" em cada remessa. O FiCC mostrava um ÚNICO botão servindo
 * `plan.boxLabelsR2Key`, que aponta só para o 1º envio: num plano dividido as
 * etiquetas dos demais envios existiam no GCS mas eram inalcançáveis, e aquelas
 * caixas seguiriam SEM etiqueta. Agora cada envio tem o seu cartão e o seu botão,
 * com download por `shipmentId`.
 *
 * Há uma janela em que o operador já chegou ao Passo 4 mas o PDF ainda não foi
 * gravado no GCS → a rota responde 404. Por isso o Passo 4 faz POLL (status do
 * workflow + prontidão POR ENVIO) e só habilita cada botão quando aquele artefato
 * existe; enquanto isso mostra "Preparando…". Se o workflow concluir SEM etiquetas
 * de caixa (sem shipmentConfirmationId), mostra mensagem precisa (não "aguarde").
 *
 * Falha NUNCA é silenciosa: o erro real é exibido (o sintoma "clico e nada
 * acontece" vinha de erro engolido + revogação imediata da Object URL — corrigida
 * em box-content-workbook.downloadBlob).
 */
const POLL_MS = 3000;
const MAX_POLLS = 40; // ~2 min

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

export function Step4Etiquetas({ planId, onReadyChange }: StepProps): ReactNode {
  const { api } = useAuth();
  const [baixandoFnsku, setBaixandoFnsku] = useState(false);
  const [baixandoBox, setBaixandoBox] = useState<string | null>(null);
  const [fnskuMsg, setFnskuMsg] = useState<string | null>(null);
  const [boxMsg, setBoxMsg] = useState<Record<string, string>>({});
  const [boxState, setBoxState] = useState<BoxState>("checking");
  const [lastStatus, setLastStatus] = useState<WorkflowStatusInfo | null>(null);
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const alive = useRef(true);
  const pollingRef = useRef(false);

  // Avançar/Finalizar nunca é bloqueado pela etiqueta de caixa (o operador pode
  // imprimi-la depois). O gate aqui é só do BOTÃO de impressão.
  useEffect(() => {
    onReadyChange(true);
  }, [onReadyChange]);

  const checkOnce = useCallback(async (): Promise<BoxState | null> => {
    try {
      // A lista de envios é a fonte autoritativa da prontidão POR REMESSA; falha
      // dela não derruba a tela (degrada para o sinal do plano).
      const [status, shipmentsRes] = await Promise.all([
        api.getWorkflowStatus(planId),
        api.getPlanShipments(planId).catch(() => null)
      ]);
      if (!alive.current) {
        return null;
      }
      const lista = shipmentsRes?.data.shipments ?? [];
      setLastStatus(status);
      setShipments(lista);
      const state = classifyBox(status, lista);
      setBoxState(state);
      return state;
    } catch {
      // Falha transitória do poll: não derruba a tela, tenta de novo no próximo tick.
      return null;
    }
  }, [api, planId]);

  const runPolling = useCallback(async (): Promise<void> => {
    if (pollingRef.current) {
      return;
    }
    pollingRef.current = true;
    try {
      const initial = await checkOnce();
      if (initial === "ready" || initial === "unavailable") {
        return;
      }
      for (let attempt = 0; attempt < MAX_POLLS && alive.current; attempt += 1) {
        await sleep(POLL_MS);
        if (!alive.current) {
          return;
        }
        const state = await checkOnce();
        if (state === "ready" || state === "unavailable") {
          return;
        }
      }
      if (alive.current) {
        setBoxState((prev) => (prev === "ready" ? prev : "stalled"));
      }
    } finally {
      pollingRef.current = false;
    }
  }, [checkOnce]);

  useEffect(() => {
    alive.current = true;
    void runPolling();
    return () => {
      alive.current = false;
    };
  }, [runPolling]);

  function atualizar(): void {
    setBoxState("checking");
    void runPolling();
  }

  async function baixarFnsku(): Promise<void> {
    setBaixandoFnsku(true);
    setFnskuMsg(null);
    await downloadFnskuLabels(api, planId, setFnskuMsg);
    setBaixandoFnsku(false);
  }

  /**
   * Baixa a etiqueta de caixa de UM envio. `shipmentId` ausente = plano
   * single-shipment / API antiga (o backend serve o path do plano).
   */
  async function baixarCaixa(shipmentId: string | null): Promise<void> {
    const chave = shipmentId ?? PLAN_CARD_KEY;
    setBaixandoBox(chave);
    setBoxMsg((prev) => ({ ...prev, [chave]: "" }));
    try {
      const { downloadBlob } = await import("../../lib/box-content-workbook.js");
      const blob = await api.downloadLabel(
        planId,
        "box",
        shipmentId ?? undefined
      );
      downloadBlob(blob, `etiquetas-caixa-${shipmentId ?? planId}.pdf`);
      setBoxMsg((prev) => ({
        ...prev,
        [chave]: "✓ Etiquetas de caixa baixadas (PDF)."
      }));
    } catch (error: unknown) {
      // Não deveria ocorrer (botão gateado por prontidão), mas se ocorrer um 404
      // de corrida, reabilita o poll em vez de mostrar um beco sem saída.
      if (error instanceof ApiFetchError && error.status === 404) {
        setBoxState("checking");
        void runPolling();
      }
      setBoxMsg((prev) => ({ ...prev, [chave]: boxErrorMessage(error) }));
    } finally {
      setBaixandoBox(null);
    }
  }

  // Um cartão por ENVIO (espelha o Seller Central). Sem envios (pré-finalize ou
  // API antiga) mantém o cartão único do plano.
  const cartoes = buildBoxLabelCards(shipments, lastStatus, boxState);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <Card title="Etiquetas de SKU (FNSKU) — colar em cada unidade">
        <p style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
          Uma etiqueta por unidade (FNSKU + título + condição). Cole em cada
          produto antes de embalar. PDF térmico 50×30 mm (uma etiqueta por página).
        </p>
        <div style={{ display: "flex", gap: "0.6rem", alignItems: "center", marginTop: "0.6rem" }}>
          <Button onClick={() => void baixarFnsku()} disabled={baixandoFnsku}>
            {baixandoFnsku ? "Baixando…" : "⬇ Baixar etiquetas de SKU (PDF)"}
          </Button>
        </div>
        {fnskuMsg !== null ? (
          <p role="status" style={{ fontSize: 12, color: "var(--text-muted)", marginTop: "0.5rem" }}>
            {fnskuMsg}
          </p>
        ) : null}
      </Card>

      {shipments.length > 1 ? (
        <Card>
          <p style={{ fontSize: 12.5, color: "var(--text-muted)", margin: 0 }}>
            A Amazon dividiu este plano em <strong>{shipments.length} envios</strong>
            . Cada envio tem a <strong>sua própria</strong> etiqueta de caixa —
            imprima as {shipments.length} e aplique em cada caixa a etiqueta do
            envio correspondente. É a mesma divisão que aparece no Seller Central.
          </p>
        </Card>
      ) : null}

      {cartoes.map((cartao) => (
        <Card key={cartao.chave} title={cartao.titulo}>
          {cartao.subtitulo !== null ? (
            <p
              style={{
                fontSize: 12,
                color: "var(--text-muted)",
                margin: "0 0 0.4rem",
                fontFamily: "var(--font-mono, monospace)"
              }}
            >
              {cartao.subtitulo}
            </p>
          ) : null}
          <p style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
            Imprima as etiquetas de caixa deste envio (térmica 102×152 mm). Cada
            caixa tem 2 etiquetas: a etiqueta FBA do conteúdo e a etiqueta da
            transportadora (rastreio + data) — as duas são necessárias para a
            coleta. Geradas pela Amazon logo após a confirmação do transporte
            (Passo 3).
          </p>

          <BoxStatusLine
            state={cartao.pronto ? "ready" : boxState}
            status={lastStatus}
            onAtualizar={atualizar}
          />

          <div style={{ marginTop: "0.6rem" }}>
            <Button
              onClick={() => void baixarCaixa(cartao.shipmentId)}
              disabled={baixandoBox !== null || !cartao.pronto}
            >
              {baixandoBox === cartao.chave
                ? "Baixando…"
                : "Imprimir etiquetas de caixa (102×152 mm)"}
            </Button>
          </div>
          {(boxMsg[cartao.chave] ?? "").length > 0 ? (
            <p role="status" style={{ fontSize: 12, color: "var(--text-muted)", marginTop: "0.5rem" }}>
              {boxMsg[cartao.chave]}
            </p>
          ) : null}
        </Card>
      ))}

      <Card title="O que vem a seguir?">
        <p style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
          Cole as etiquetas de SKU nas unidades e as de caixa nas caixas, depois
          finalize para acompanhar o rastreamento.
        </p>
      </Card>
    </div>
  );
}

/**
 * Linha de status da etiqueta de caixa: traduz o `BoxState` em feedback claro e,
 * quando faz sentido, oferece "Atualizar" para rechecar sem recarregar a página.
 */
function BoxStatusLine({
  state,
  status,
  onAtualizar
}: {
  state: BoxState;
  status: WorkflowStatusInfo | null;
  onAtualizar: () => void;
}): ReactNode {
  if (state === "ready") {
    return (
      <div style={{ marginTop: "0.6rem" }}>
        <Badge tone="green">✓ Etiquetas de caixa prontas</Badge>
      </div>
    );
  }

  const wrap = (node: ReactNode, withRefresh = true): ReactNode => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "0.6rem",
        marginTop: "0.6rem",
        flexWrap: "wrap"
      }}
    >
      <p role="status" style={{ fontSize: 12.5, color: "var(--text-muted)", margin: 0 }}>
        {node}
      </p>
      {withRefresh ? (
        <Button variant="secondary" onClick={onAtualizar}>
          Atualizar
        </Button>
      ) : null}
    </div>
  );

  if (state === "checking") {
    return wrap("Verificando se as etiquetas de caixa estão prontas…", false);
  }

  if (state === "preparing" || state === "partial") {
    return wrap(
      "Preparando as etiquetas de caixa deste envio… A Amazon está gerando as etiquetas após a confirmação do transporte. Isso leva alguns segundos."
    );
  }

  if (state === "stalled") {
    return wrap(
      "As etiquetas de caixa ainda estão sendo geradas pela Amazon (está demorando mais que o normal). Clique em Atualizar para verificar de novo."
    );
  }

  // unavailable: o workflow terminou sem etiquetas de caixa.
  const wf = status?.workflowStatus ?? "";
  if (TERMINAL_WORKFLOW_STATUSES.has(wf)) {
    // O motivo real já vem no status — mostrá-lo evita o "cancelado ou falhou"
    // sem causa, que obrigava o operador a caçar a explicação em outro lugar.
    const motivo =
      typeof status?.failureReason === "string" && status.failureReason.length > 0
        ? ` Motivo: ${status.failureReason}`
        : "";
    return wrap(
      `Este envio foi cancelado ou falhou — não há etiquetas de caixa para imprimir.${motivo}`,
      false
    );
  }
  return wrap(
    "As etiquetas de caixa não foram geradas para este envio (a Amazon não retornou o ID de confirmação da remessa). Verifique a remessa no Seller Central e tente Atualizar."
  );
}

/**
 * Baixa o PDF de etiquetas FNSKU; em 404 gera (lazy) e re-baixa. Compartilha o
 * comportamento do botão do Passo 2 (GrupoEmbalagemModal). Erro real é exibido.
 */
async function downloadFnskuLabels(
  api: ApiClient,
  planId: string,
  setMessage: (m: string) => void
): Promise<void> {
  try {
    const { downloadBlob } = await import("../../lib/box-content-workbook.js");
    let blob: Blob;
    try {
      blob = await api.downloadLabel(planId, "fnsku");
    } catch (error) {
      if (error instanceof ApiFetchError && error.status === 404) {
        setMessage("Gerando etiquetas FNSKU…");
        await api.generateFnskuLabels(planId);
        // Re-baixa com re-tentativas curtas: blinda a corrida de 404 logo após o
        // generate (mesmo comportamento do botão do Passo 2 — GrupoEmbalagemModal).
        blob = await downloadLabelWithRetry(api, planId);
      } else {
        throw error;
      }
    }
    downloadBlob(blob, `etiquetas-sku-${planId}.pdf`);
    setMessage("✓ Etiquetas de SKU baixadas (PDF).");
  } catch (error: unknown) {
    setMessage(fnskuErrorMessage(error));
  }
}

/**
 * Baixa o PDF de etiquetas FNSKU com re-tentativas curtas em 404 — chamado logo
 * após `generateFnskuLabels` (que persiste o path antes de responder 201). Blinda
 * contra qualquer corrida/latência residual (espelha o helper do Passo 2).
 */
async function downloadLabelWithRetry(
  api: ApiClient,
  planId: string,
  attempts = 4
): Promise<Blob> {
  let lastError: unknown = null;
  for (let i = 0; i < attempts; i += 1) {
    try {
      return await api.downloadLabel(planId, "fnsku");
    } catch (error) {
      lastError = error;
      if (!(error instanceof ApiFetchError && error.status === 404)) {
        throw error;
      }
      if (i < attempts - 1) {
        await sleep(800);
      }
    }
  }
  throw lastError;
}

function fnskuErrorMessage(error: unknown): string {
  if (error instanceof ApiFetchError) {
    if (error.code === "FNSKU_LABELS_NOT_APPLICABLE") {
      return "Etiquetas FNSKU não se aplicam: nenhum item com etiquetagem pelo vendedor (labelOwner = AMAZON).";
    }
    if (error.status === 401 || error.status === 403) {
      return "Sem permissão para gerar/baixar as etiquetas FNSKU (faça login como Admin/Operador).";
    }
    return `Falha ao baixar as etiquetas FNSKU (HTTP ${error.status}${
      error.code.length > 0 ? ` · ${error.code}` : ""
    }): ${error.message}`;
  }
  return error instanceof Error
    ? `Falha ao baixar as etiquetas FNSKU: ${error.message}`
    : "Falha ao baixar as etiquetas FNSKU.";
}

function boxErrorMessage(error: unknown): string {
  if (error instanceof ApiFetchError) {
    if (error.status === 404) {
      return "Etiquetas de caixa ainda não disponíveis — a Amazon ainda está gerando. Aguarde alguns segundos e clique em Atualizar.";
    }
    if (error.status === 401 || error.status === 403) {
      return "Sem permissão para baixar as etiquetas de caixa.";
    }
    return `Falha ao baixar as etiquetas de caixa (HTTP ${error.status}${
      error.code.length > 0 ? ` · ${error.code}` : ""
    }): ${error.message}`;
  }
  return error instanceof Error
    ? `Falha ao baixar as etiquetas de caixa: ${error.message}`
    : "Falha ao baixar as etiquetas de caixa.";
}
